// contexts/DashboardContext.tsx
'use client';

import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useState,
  useCallback,
} from 'react';
import {
  SymbolInfo as WatchlistSymbolInfo,
  WatchlistItem,
} from '@/components/component/dashboard/watchlist/watchlist.types';

// Re-export SymbolInfo from watchlist.types for compatibility
export type SymbolInfo = WatchlistSymbolInfo;

// Define the shape of the dashboard state
export interface DashboardState {
  selectedSymbol: string;
  timeframe: '1D' | '1W' | '1M' | '3M' | '1Y' | 'ALL';
  source: 'ebay' | 'forum' | 'reddit' | 'all';
  viewMode: 'chart' | 'watchlist' | 'transactions';
  isLoading: boolean;
}

// Define possible actions for the dashboard
type DashboardAction =
  | { type: 'SET_SYMBOL'; payload: string }
  | { type: 'SET_TIMEFRAME'; payload: '1D' | '1W' | '1M' | '3M' | '1Y' | 'ALL' }
  | { type: 'SET_SOURCE'; payload: 'ebay' | 'forum' | 'reddit' | 'all' }
  | { type: 'SET_VIEW_MODE'; payload: 'chart' | 'watchlist' | 'transactions' }
  | { type: 'SET_LOADING'; payload: boolean };

// Source availability mapping
const SOURCE_AVAILABILITY: Record<string, boolean> = {
  forum: true,
  reddit: true,
  ebay: true,
  grailzee: false,
  chrono: false,
};

// Initial state
const initialState: DashboardState = {
  selectedSymbol: 'ROLEX:16610',
  timeframe: '1M',
  source: 'all',
  viewMode: 'chart',
  isLoading: true,
};

// Create the context with default values
interface DashboardContextType {
  state: DashboardState;
  dispatch: React.Dispatch<DashboardAction>;
  watchlistItems: WatchlistItem[];
  setWatchlistItems: React.Dispatch<React.SetStateAction<WatchlistItem[]>>;
  selectedSymbol: SymbolInfo | null;
  datafeed: any;
  medianPrice: number;
  latestPrice: number | null;
  invalidateCache: () => void;
  changeSource: (source: string) => void;
  sourceAvailability: Record<string, boolean>;
  setSelectedSymbolInfo: (info: SymbolInfo | null) => void;
}

// Create context with default values
const DashboardContext = createContext<DashboardContextType>({
  state: initialState,
  dispatch: () => null,
  watchlistItems: [],
  setWatchlistItems: () => null,
  selectedSymbol: null,
  datafeed: null,
  medianPrice: 0,
  latestPrice: null,
  invalidateCache: () => null,
  changeSource: () => null,
  sourceAvailability: SOURCE_AVAILABILITY,
  setSelectedSymbolInfo: () => null,
});

// Reducer function to handle state updates
function dashboardReducer(
  state: DashboardState,
  action: DashboardAction
): DashboardState {
  switch (action.type) {
    case 'SET_SYMBOL':
      return { ...state, selectedSymbol: action.payload };
    case 'SET_TIMEFRAME':
      return { ...state, timeframe: action.payload };
    case 'SET_SOURCE':
      return { ...state, source: action.payload };
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    default:
      return state;
  }
}

// Provider component that wraps parts of the app that need dashboard state
export function DashboardProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(dashboardReducer, initialState);
  const [watchlistItems, setWatchlistItems] = useState<WatchlistItem[]>([]);
  const [selectedSymbolInfo, setSelectedSymbolInfo] =
    useState<SymbolInfo | null>({
      name: initialState.selectedSymbol,
      description: 'Submariner 16610',
      exchange: 'Rolex',
    });
  const [medianPrice, setMedianPrice] = useState<number>(0);
  const [latestPrice, setLatestPrice] = useState<number | null>(null);

  // Get datafeed from window if available
  const datafeed =
    typeof window !== 'undefined'
      ? (window as any).globalDatafeedInstance
      : null;

  // Cache invalidation function
  const invalidateCache = useCallback(() => {
    console.log('Invalidating data cache');
    if (datafeed && typeof datafeed.clearCache === 'function') {
      datafeed.clearCache();
    }
  }, [datafeed]);

  // Function to change data source
  const changeSource = useCallback(
    (source: string) => {
      if (!datafeed || !SOURCE_AVAILABILITY[source]) return;

      console.log('Changing data source to:', source);

      // Update state
      dispatch({ type: 'SET_SOURCE', payload: source as any });

      // Update datafeed source
      if (typeof datafeed.setCurrentSource === 'function') {
        // First clear the cache
        invalidateCache();

        // Then set the new source
        datafeed.setCurrentSource(source);

        console.log(`Data source changed to: ${source}`);
      }
    },
    [datafeed, invalidateCache]
  );

  return (
    <DashboardContext.Provider
      value={{
        state,
        dispatch,
        watchlistItems,
        setWatchlistItems,
        selectedSymbol: selectedSymbolInfo,
        datafeed,
        medianPrice,
        latestPrice,
        invalidateCache,
        changeSource,
        sourceAvailability: SOURCE_AVAILABILITY,
        setSelectedSymbolInfo,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
}

// Custom hook to use the dashboard context
export function useDashboard() {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
}
